import React, { useState, useMemo } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material/";
import { useSelector, useDispatch } from "react-redux";
import { groupBy } from "lodash";
import { strings } from "../../../common/i18n";
import { enums, helpers } from "../../../common/lib";
import { v4 as uuidv4 } from "uuid";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import SyncIcon from "@mui/icons-material/Sync";
import { Delete, Email } from "@mui/icons-material";
import actions from "../../../Store/actions";
import { logToSentry } from "../../../config/Sentry";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/**
 *  Hook for user table data and operations
 */

export default () => {
    const {
        accounts,
        assetAuditors,
        altAssets,
        auditAssignments,
        auditLocations,
        locations,
        userOrganisations,
        typesList
    } = useSelector((state) => state);

    const dispatch = useDispatch();
    const [loadingTable, setLoadingTableData] = useState(true);
    const [recalling, setRecalling] = useState(false);

    const orgId = userOrganisations.item.id;

    /** Can recall/reassign the assignemnt */
    const canRecall = (statusId) =>
        statusId === enums.status.auditAssignment.pendingDownload;

    /** Can send the assignemnt  */
    const canSendReminder = (statusId) =>
        statusId === enums.status.auditAssignment.pendingSubmission ||
        statusId === enums.status.auditAssignment.pendingDownload;

    const handleRecall = (row) => async () => {
        setRecalling(true);
        try {
            const next = {
                id: row.auditAssignmentId,
                status: enums.status.auditAssignment.recalled
            };

            await dispatch(
                actions.auditAssignments.create.bulkUpdatePostRequest([next])
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On recalling audit assignment:", { error });
            dispatch(actions.toast.create.error(strings("errors.onRecall")));
            console.error(error);
        }
        setRecalling(false);
    };

    const handleReminder = (auditAssignmentId: number) => async () => {
        try {
            await dispatch(
                actions.auditResendAssignmentEmail.create.getRequest(
                    `assignment_id=${auditAssignmentId}`
                )
            );
            dispatch(
                actions.toast.create.success(strings("messages.reminderEmail"))
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On reminder email for audit assignment:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onReminderEmail"))
            );
            console.error(error);
        }
    };
    const handleRestAuditAssignment = (auditAssignmentId) => async () => {
        try {
            await dispatch(
                actions.auditAssignments.create.patchRequest(
                    auditAssignmentId,
                    orgId,
                    {
                        status: 1
                    }
                )
            );
            dispatch(
                actions.toast.create.success(
                    strings("messages.resetAuditAssignment")
                )
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On reset audit assignment:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onresetAssignment"))
            );
            console.error(error);
        }
    };

    /** Remove Duplicates */
    const removeDuplicates = (array) => {
        const uniqueIds = {};

        return array.reduce((result, current) => {
            const id = current.auditAsset;
            if (
                !uniqueIds[id] ||
                new Date(current.assignedDate) >
                    new Date(uniqueIds[id].assignedDate)
            ) {
                uniqueIds[id] = current;
            }
            return Object.values(uniqueIds);
        }, []);
    };

    const data = useMemo(() => {
        setLoadingTableData(true);

        const assetAuditorsData = removeDuplicates(assetAuditors.data);

        const byAuditor = groupBy(assetAuditorsData, (item) => item.auditor);
        const auditorAssigned = Object.entries(byAuditor).map(
            ([key, value]) => {
                const account = helpers.getListItemForId(accounts, key);
                const res = {
                    seen: 0,
                    altAssets: 0,
                    excluded: 0
                };
                res.altAssets = altAssets.data.filter(
                    (item) => account && account.id === item.createdBy
                ).length;

                value.map((item) => {
                    if (item.exclusionReason) res.excluded += 1;
                    if (!item.exclusionReason) res.seen += 1;
                });

                return {
                    id: key,
                    email: account ? account.email : "",
                    ...res,
                    count: value.length + res.altAssets
                };
            }
        );
        const { auditAssignmentStatus } = typesList;
        // Pre-reference lookup structures once — avoids repeated function-call
        // overhead inside the per-row map (getListItemForId is O(1) via byId).
        const auditLocationsById = auditLocations.byId;
        const auditLocationsData = auditLocations.data;
        const accountsById = accounts.byId;
        const accountsData = accounts.data;
        const locationsById = locations.byId;
        const locationsData = locations.data;
        const statusById = auditAssignmentStatus.byId;
        const statusData = auditAssignmentStatus.data;
        const result = auditAssignments.data.map((item, idx) => {
            const id = uuidv4();
            const auditLocation = auditLocationsData[auditLocationsById[item.auditLocation]];
            const auditor = accountsData[accountsById[item.auditor]];
            const location = auditLocation
                ? locationsData[locationsById[auditLocation.location]]
                : null;
            const status = statusData[statusById[item.status]];
            const auditorDetails =
                auditor &&
                auditorAssigned.find((item) => item.email === auditor.email);
            return {
                id,
                dataIndex: idx,
                auditAssignmentId: item.id,
                auditorAssigned,
                isReissued: item.isReissued,
                auditor: auditor
                    ? `${auditor.displayName}${auditor.email}`
                    : "",
                displayName: auditor ? auditor.displayName : "",
                downloadInformation:
                    item.downloadInformation &&
                    item.downloadInformation.split("::").shift(),
                email: auditor ? auditor.email : "",
                location: location ? location.name : "",
                auditStatus: status?.name ?? "",
                seen: auditorDetails ? auditorDetails.seen : "",
                excluded: auditorDetails ? auditorDetails.excluded : "",
                altAssets: auditorDetails ? auditorDetails.altAssets : "",
                count: auditorDetails ? auditorDetails.count : "",
                statusId: status?.id ?? null
            };
        });
        setLoadingTableData(false);
        return result;
    }, [
        accounts,
        auditAssignments,
        auditLocations,
        locations,
        typesList,
        altAssets
    ]);

    /** On columns  */
    const columns = useMemo(() => {
        const statusFilter = typesList.auditAssignmentStatus.data.map(
            (item) => item.name
        );

        return [
            {
                accessorKey: "location",
                header: strings("location"),
                enableColumnFilter: false
            },
            {
                accessorKey: "auditor",
                header: strings("auditor"),
                enableHiding: false,
                enableColumnFilter: false,
                Cell: ({ row }) => {
                    const { displayName, email } = row.original;
                    return (
                        <Box>
                            <Typography variant="caption" component="div">
                                {displayName}
                            </Typography>
                            <Typography variant="caption" component="div">
                                {email}
                            </Typography>
                        </Box>
                    );
                }
            },
            {
                accessorKey: "seen",
                header: strings("seen"),
                enableSorting: false,
                Header: () => <CheckIcon fontSize="small" color="success" />
            },
            {
                accessorKey: "excluded",
                header: strings("excluded"),
                enableSorting: false,
                Header: () => (
                    <VisibilityOffIcon fontSize="small" color="error" />
                )
            },
            {
                accessorKey: "altAssets",
                header: strings("unexpected"),
                enableSorting: false,
                Header: () => (
                    <ErrorOutlineIcon fontSize="small" color="warning" />
                )
            },
            {
                accessorKey: "count",
                header: strings("total"),
                enableSorting: false
            },
            {
                accessorKey: "auditStatus",
                header: strings("status"),
                filterVariant: "multi-select",
                filterSelectOptions: statusFilter
            },
            {
                accessorKey: "downloadInformation",
                header: strings("downloadedOn"),
                enableColumnFilter: false
            },
            {
                id: "reissued",
                accessorKey: "isReissued",
                header: strings("reissued"),
                enableSorting: false,
                enableColumnFilter: false,
                enableGlobalFilter: false,
                Cell: ({ row }) =>
                    row.original.isReissued ? (
                        <Tooltip title={strings("reissued")}>
                            <SyncIcon color="primary" />
                        </Tooltip>
                    ) : null
            },
            {
                id: "actions",
                accessorFn: () => "",
                header: strings("actions"),
                enableSorting: false,
                enableColumnFilter: false,
                enableGlobalFilter: false,
                Cell: ({ row }) => (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {canSendReminder(row.original.statusId) ? (
                            <IconButton
                                size="small"
                                onClick={handleReminder(
                                    row.original.auditAssignmentId
                                )}
                            >
                                <Tooltip title={strings("reminder")}>
                                    <Email fontSize="small" color="primary" />
                                </Tooltip>
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 30 }} />
                        )}

                        {canRecall(row.original.statusId) ? (
                            <IconButton
                                size="small"
                                disabled={recalling}
                                onClick={handleRecall(row.original)}
                            >
                                <Tooltip title={strings("recall")}>
                                    <Delete fontSize="small" color="primary" />
                                </Tooltip>
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 30 }} />
                        )}

                        {canSendReminder(row.original.statusId) ? (
                            <IconButton
                                size="small"
                                onClick={handleRestAuditAssignment(
                                    row.original.auditAssignmentId
                                )}
                            >
                                <Tooltip title={strings("resetAuditAssignment")}>
                                    <SettingsBackupRestoreIcon fontSize="small" color="primary" />
                                </Tooltip>
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 30 }} />
                        )}
                    </Box>
                )
            }
        ];
    }, [auditAssignments, recalling, typesList]);

    const initialState = {
        columnVisibility: {
            location: false,
            reissued: false
        }
    };

    return {
        loadingTable,
        columns,
        data,
        initialState
    };
};
